package com.dualremotetest


// Two-remote input routing for the game. Identity rules come from the field tests in docs/FINDINGS.md:
// a player is bound to a Channel (deviceId, plus source for injected deviceId -1 events such as HDMI-CEC),
// held state is tracked per player, and stable descriptors re-bind a reconnected physical remote.
// Plain Kotlin (Android types only for constants) so it is unit-tested on the JVM.

class CoopPlayerInput(val number: Int) {
    var channel: Channel? = null
        internal set
    var descriptor: String? = null
        internal set
    var deviceName: String = ""
        internal set
    var connected: Boolean = true
        internal set

    /** Physically held directions in press order -> event time of the DOWN. */
    internal val held = LinkedHashMap<Int, Long>()
    /** Grid mode: direction presses waiting to become one-cell steps. */
    internal val stepRequests = ArrayDeque<Int>()
    /** Manual fire: set by a fresh OK press, consumed (shot or refused) by the next simulation step. */
    internal var fireRequested = false
    /** Fire keys held down now -> event time of the DOWN. Holding one keeps firing (see TankMatch.HOLD_FIRE_MS). */
    internal val fireHeld = HashMap<Int, Long>()

    /** When the fire key held longest went down, or null when none is held. */
    fun fireHeldSince(): Long? = fireHeld.values.minOrNull()

    /** Experimental smoothing: a released direction that stays active until [graceUntil]. */
    var graceKey: Int? = null
        private set
    private var graceStart = 0L
    private var graceUntil = 0L

    // Observations for diagnostics.
    var downs = 0; internal set
    var ups = 0; internal set
    var repeats = 0; internal set
    /** Heuristic: same direction re-pressed ≤ QUICK_REPRESS_MS after its UP. */
    var quickRepresses = 0; internal set
    /** Heuristic: same direction re-pressed within QUICK_REPRESS_MS..SHORT_REPRESS_MS after its UP. */
    var shortRepresses = 0; internal set
    var suspectedTimeoutReleases = 0; internal set
    /** Smoothing: re-presses that arrived inside the grace period (movement was not interrupted). */
    var bridgedGaps = 0; internal set
    /** Smoothing: grace periods that ran out with no re-press (movement continued after a real release). */
    var graceExpiries = 0; internal set
    var graceMovementMs = 0L; internal set
    /** Smoothing: grace periods cut short by an OK press, so firing never extends a released direction. */
    var graceEndedByFire = 0; internal set
    /** OK: fresh presses (each one asks for a shot) and auto-repeats (never fire). */
    var okPresses = 0; internal set
    var okRepeatsIgnored = 0; internal set
    /**
     * Heuristic: fresh OK press ≤ [CoopInput.OK_AFTER_TIMEOUT_WINDOW_MS] after an OK UP that looked like the CEC safety
     * timeout. Either the TV re-sent a button that was still held (one hold firing twice) or a fast re-tap.
     */
    var okPressesAfterSuspectedTimeout = 0; internal set
    var lastEvent: String = ""; internal set

    private val lastUpAt = HashMap<Int, Long>()
    private val lastMessageAt = HashMap<Int, Long>()
    private val suspectedTimeoutUpAt = HashMap<Int, Long>()

    val isAssigned get() = channel != null
    val isHdmi get() = channel?.let { it.isInjected && hasSource(it.source, Devices.SOURCE_HDMI) } == true

    fun heldDirections(): List<Int> = held.keys.toList()

    /** Most recent held direction; otherwise the smoothed direction while its grace period lasts. */
    fun effectiveDirection(now: Long): Int? {
        held.keys.lastOrNull()?.let { return it }
        val g = graceKey ?: return null
        return if (now < graceUntil) g else null
    }

    fun channelText(): String = channel?.let { "${it.label} · $deviceName" } ?: "unassigned"

    internal fun bind(ch: Channel, dev: DeviceInfo?) {
        channel = ch
        descriptor = if (ch.isInjected) null else dev?.descriptor
        deviceName = when {
            isTouchChannel(ch) -> "on-screen controls"
            ch.isInjected -> "${dev?.name ?: "Virtual"} via ${sourceShort(ch.source)}"
            else -> dev?.name ?: "unknown"
        }
        connected = true
        clearHeld()
    }

    internal fun unbind() {
        channel = null; descriptor = null; deviceName = ""; connected = true
        clearHeld()
    }

    internal fun markDisconnected() {
        connected = false
        clearHeld()
    }

    internal fun rebindTo(dev: DeviceInfo) {
        channel = Channel.ofDevice(dev.id)
        deviceName = dev.name
        connected = true
    }

    fun clearHeld() {
        held.clear()
        stepRequests.clear()
        graceKey = null
        fireRequested = false
        fireHeld.clear()
    }

    internal fun resetStats() {
        downs = 0; ups = 0; repeats = 0; quickRepresses = 0; shortRepresses = 0; suspectedTimeoutReleases = 0
        bridgedGaps = 0; graceExpiries = 0; graceMovementMs = 0; graceEndedByFire = 0
        okPresses = 0; okRepeatsIgnored = 0; okPressesAfterSuspectedTimeout = 0
        lastUpAt.clear(); lastMessageAt.clear(); suspectedTimeoutUpAt.clear()
    }

    /** Ends an expired grace period; returns true if one expired (movement continued for the whole period). */
    internal fun expireGrace(now: Long): Boolean {
        if (graceKey == null || now < graceUntil) return false
        graceExpiries++
        graceMovementMs += graceUntil - graceStart
        graceKey = null
        return true
    }

    /** Applies one direction event. Returns a heuristic observation worth logging, if any. */
    internal fun applyDirection(e: KeyInput, smoothingMs: Long): String? {
        val key = e.keyCode
        val t = e.eventTime
        lastEvent = "${clockTime(e.wallTime)} ${e.keyName} ${actionName(e.action)}${if (e.repeatCount > 0) " r${e.repeatCount}" else ""}"
        var note: String? = null
        if (e.isDown) {
            lastMessageAt[key] = t
            if (e.repeatCount > 0) {
                repeats++
                // Repeats never drive movement; they only restore a hold that a pause or focus change cleared.
                if (!held.containsKey(key)) held[key] = t
                return null
            }
            downs++
            lastUpAt[key]?.let { up ->
                val gap = t - up
                if (gap <= QUICK_REPRESS_MS) {
                    quickRepresses++
                    note = "P$number ${keyLabel(key)} re-pressed $gap ms after release (heuristic: re-sent hold or very fast tap)"
                } else if (gap <= SHORT_REPRESS_MS) {
                    shortRepresses++
                }
            }
            graceKey?.let { g ->
                if (g == key && t < graceUntil) {
                    bridgedGaps++
                    graceMovementMs += t - graceStart
                }
                graceKey = null // any new press ends the grace period; a different direction applies immediately
            }
            held.remove(key)
            held[key] = t
            if (stepRequests.size < 2) stepRequests.addLast(key)
        } else if (e.isUp) {
            ups++
            val downAt = held.remove(key)
            if (downAt != null) note = checkTimeoutRelease(key, t, lastMessageAt[key] ?: downAt)
            lastMessageAt.remove(key)
            lastUpAt[key] = t
            if (downAt != null && smoothingMs > 0 && held.isEmpty() && !e.isCanceled) {
                graceKey = key
                graceStart = t
                graceUntil = t + smoothingMs
            }
        }
        return note
    }

    /**
     * Applies one OK event. Only a fresh DOWN (repeatCount 0) requests a shot; auto-repeats of a held OK never fire.
     * Held directions are untouched: a remote that really delivers direction + OK keeps moving, but a released
     * direction is not kept alive by smoothing while firing.
     */
    internal fun applyFire(e: KeyInput): String? {
        val key = e.keyCode
        val t = e.eventTime
        lastEvent = "${clockTime(e.wallTime)} ${e.keyName} ${actionName(e.action)}${if (e.repeatCount > 0) " r${e.repeatCount}" else ""}"
        var note: String? = null
        if (e.isDown) {
            lastMessageAt[key] = t
            if (e.repeatCount > 0) {
                okRepeatsIgnored++
                return null
            }
            okPresses++
            suspectedTimeoutUpAt.remove(key)?.let { up ->
                if (t - up <= CoopInput.OK_AFTER_TIMEOUT_WINDOW_MS) {
                    okPressesAfterSuspectedTimeout++
                    note = "P$number ${keyLabel(key)} pressed ${t - up} ms after a suspected CEC timeout UP " +
                        "(heuristic: the TV re-sent a held button, or a fast re-tap) – fired as a new press"
                }
            }
            graceKey?.let {
                graceEndedByFire++
                graceMovementMs += t - graceStart
                graceKey = null
            }
            fireRequested = true
            fireHeld[key] = t
        } else if (e.isUp) {
            fireHeld.remove(key)
            val last = lastMessageAt.remove(key)
            if (last != null) {
                note = checkTimeoutRelease(key, t, last)
                if (note != null) suspectedTimeoutUpAt[key] = t
            }
            lastUpAt[key] = t
        }
        return note
    }

    private fun checkTimeoutRelease(key: Int, t: Long, lastMessage: Long): String? {
        if (!looksLikeCecTimeoutRelease(channel, t - lastMessage)) return null
        suspectedTimeoutReleases++
        return "P$number ${keyLabel(key)} UP ~550 ms after the last CEC message (suspected timeout release, unconfirmed)"
    }
}

enum class AssignStep { PLAYER1, PLAYER2, DONE }

/**
 * Fire / confirm keys for the game: remote OK (DPAD_CENTER), Enter, numpad Enter (AOSP maps the CEC "Enter" command to
 * it), Space, and a controller's A and X. A keyboard's Enter and Space come from the same deviceId, so they belong to
 * the same player.
 */
val FIRE_KEYS = OK_KEYS + Keys.KEYCODE_SPACE + GAMEPAD_FIRE_KEYS

class CoopInput(private val log: (String) -> Unit) {
    val p1 = CoopPlayerInput(1)
    val p2 = CoopPlayerInput(2)
    val players = listOf(p1, p2)

    /** Experimental smoothing grace period for short release/re-press gaps; 0 = off. Directions only. */
    var smoothingMs: Long = 0L
    var deviceLookup: (Int) -> DeviceInfo? = { null }

    /** Shown during assignment, for example a rejected same-remote press. */
    var assignMessage: String? = null
        private set
    var sameChannelRejects = 0
        private set
    var unassignedInputs = 0
        private set
    /** Assignments of the previous match, shown as a hint on the ready screen. */
    var previousAssignments: List<String> = emptyList()
        private set

    private val swallowUps = HashSet<Pair<Channel, Int>>()

    val step: AssignStep
        get() = when {
            !p1.isAssigned -> AssignStep.PLAYER1
            !p2.isAssigned -> AssignStep.PLAYER2
            else -> AssignStep.DONE
        }

    fun resetAssignments() {
        if (p1.isAssigned || p2.isAssigned) previousAssignments = players.map { "P${it.number}: ${it.channelText()}" }
        players.forEach { it.unbind() }
        swallowUps.clear()
        assignMessage = null
    }

    fun resetStats() {
        players.forEach { it.resetStats() }
        sameChannelRejects = 0
        unassignedInputs = 0
    }

    fun clearAllHeld() = players.forEach { it.clearHeld() }

    /** Online play: each player is bound to the session's channel for that player, never to a device. */
    fun bindOnline(localPlayer: Int) {
        resetAssignments()
        players.forEachIndexed { i, p ->
            p.bind(Channel(ONLINE_DEVICE_IDS[i], 0), null)
            p.deviceName = if (i == localPlayer) "online, this phone" else "online, the other phone"
        }
        log("TANK online: this phone is Player ${localPlayer + 1}")
    }

    /** Assignment phase. Returns true when this event assigned a player. Only a fresh OK DOWN assigns. */
    fun onAssignKey(e: KeyInput): Boolean {
        val ch = Channel.of(e)
        if (e.isUp && swallowUps.remove(ch to e.keyCode)) return false
        if (!e.isDown || e.repeatCount != 0 || e.keyCode !in FIRE_KEYS) return false
        // A remote that reconnected with a new deviceId belongs to its original player, not to the next free slot.
        if (players.none { it.channel == ch } && rebindByDescriptor(ch) != null) {
            swallowUps.add(ch to e.keyCode)
            return false
        }
        val target = when (step) {
            AssignStep.PLAYER1 -> p1
            AssignStep.PLAYER2 -> p2
            AssignStep.DONE -> return false
        }
        swallowUps.add(ch to e.keyCode)
        if (target === p2 && ch == p1.channel) {
            sameChannelRejects++
            assignMessage = "That press came from Player 1's remote (${ch.label}). Player 2: press OK on the OTHER remote."
            log("TANK assign: rejected Player 2 on ${ch.label} – same input channel as Player 1")
            return false
        }
        val dev = deviceLookup(e.deviceId)
        target.bind(ch, dev)
        assignMessage = null
        log("TANK assign: Player ${target.number} → ${ch.label} \"${target.deviceName}\" (${channelKind(ch, dev)})" +
            (target.descriptor?.let { ", descriptor $it" } ?: ""))
        return true
    }

    /** Gameplay phase. Directions update the owning player's held state; a fresh OK press requests a shot. */
    fun onGameKey(e: KeyInput) {
        val ch = Channel.of(e)
        if (e.isUp && swallowUps.remove(ch to e.keyCode)) return
        if ((e.keyCode !in DIRECTION_KEYS && e.keyCode !in FIRE_KEYS) || (!e.isDown && !e.isUp)) return
        var owner = players.firstOrNull { it.channel == ch && it.connected }
        if (owner == null) owner = rebindByDescriptor(ch)
        if (owner == null) {
            if (e.isDown && e.repeatCount == 0) {
                unassignedInputs++
                log("TANK input from unassigned channel ${ch.label} (${keyLabel(e.keyCode)}) ignored")
            }
            return
        }
        val note = if (e.keyCode in FIRE_KEYS) owner.applyFire(e) else owner.applyDirection(e, smoothingMs)
        note?.let { log("TANK $it") }
    }

    /** Menus: the UP of a press that assigned a player is not passed on, so drop its swallow marker here. */
    fun menuKeyUp(e: KeyInput) {
        if (e.isUp) swallowUps.remove(Channel.of(e) to e.keyCode)
    }

    /** Drops OK presses made before the match started (countdown), so nobody fires on GO. */
    fun clearFireRequests() = players.forEach { it.fireRequested = false }

    /** Ends expired smoothing grace periods; call once per frame with the input clock (uptime). */
    fun expireGrace(now: Long) = players.forEach { it.expireGrace(now) }

    /**
     * Re-checks bindings against the current device list. Returns the players whose remote has just
     * disconnected. A deviceId -1 (HDMI-CEC) channel can never be detected as disconnected.
     */
    fun syncDevices(devices: List<DeviceInfo>): List<CoopPlayerInput> {
        val lost = mutableListOf<CoopPlayerInput>()
        for (p in players) {
            val ch = p.channel ?: continue
            if (ch.isInjected) continue
            if (p.connected && devices.none { it.id == ch.deviceId }) {
                p.markDisconnected()
                lost += p
                log("TANK Player ${p.number} remote DISCONNECTED (deviceId ${ch.deviceId}, \"${p.deviceName}\")")
            }
        }
        for (p in players) {
            val descriptor = p.descriptor
            if (p.connected || descriptor == null) continue
            val bound = players.filter { it.connected }.mapNotNull { it.channel?.deviceId }.toSet()
            val candidates = devices.filter { it.descriptor == descriptor && it.id !in bound }
            val otherWaiting = players.any { it !== p && !it.connected && it.descriptor == descriptor }
            if (candidates.size == 1 && !otherWaiting) reconnect(p, candidates.single())
            else if (candidates.isNotEmpty()) log("TANK reconnection ambiguous for descriptor $descriptor – reassign remotes")
        }
        return lost
    }

    private fun rebindByDescriptor(ch: Channel): CoopPlayerInput? {
        if (ch.isInjected) return null
        val dev = deviceLookup(ch.deviceId) ?: return null
        val waiting = players.filter { !it.connected && it.descriptor != null && it.descriptor == dev.descriptor }
        if (waiting.size != 1) return null
        reconnect(waiting.single(), dev)
        return waiting.single()
    }

    private fun reconnect(p: CoopPlayerInput, dev: DeviceInfo) {
        val old = p.channel?.deviceId
        p.rebindTo(dev)
        log("TANK Player ${p.number} remote RECONNECTED as deviceId ${dev.id} (was $old), matched by descriptor")
    }

    companion object {
        val SMOOTHING_CHOICES = listOf(0L, 100L, 150L, 175L, 200L, 250L)
        /** Window for the "OK pressed again right after a suspected CEC timeout UP" heuristic. */
        const val OK_AFTER_TIMEOUT_WINDOW_MS = 250L
    }
}

fun directionDelta(key: Int): Pair<Int, Int> = when (key) {
    Keys.KEYCODE_DPAD_UP -> 0 to -1
    Keys.KEYCODE_DPAD_DOWN -> 0 to 1
    Keys.KEYCODE_DPAD_LEFT -> -1 to 0
    else -> 1 to 0
}

fun opposite(key: Int): Int = when (key) {
    Keys.KEYCODE_DPAD_UP -> Keys.KEYCODE_DPAD_DOWN
    Keys.KEYCODE_DPAD_DOWN -> Keys.KEYCODE_DPAD_UP
    Keys.KEYCODE_DPAD_LEFT -> Keys.KEYCODE_DPAD_RIGHT
    else -> Keys.KEYCODE_DPAD_LEFT
}
